import { addPointModeAtom, pickedPointAtom, userViewerSettingsAtom } from '@/atoms';
import { GetProjectQuery } from '@/graphql/graphql';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import {
  IClassification,
  IPointCloudTreeNode,
  PointCloudMaterial,
  PointCloudOctree,
  PointCloudOctreeNode,
  PointColorType,
  PointShape,
  PointSizeType,
  Potree,
  QueueItem,
} from '@pnext/three-loader';
import { NodeLoader } from '@pnext/three-loader/build/declarations/loading2/octree-loader';
import type { CameraControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState, type RefObject } from 'react';
import * as THREE from 'three';
import { LayerData } from '../atoms/layers.atom';
import AreaMeasuringTool from './3D/AreaMeasuringTool';
import MeasuringTool from './3D/MeasuringTool';
import { PointCloudBoundingBoxes } from './3D/PointCloudBoundingBoxes';
import PointX from './3D/PointX';
import SavedMeasurements from './3D/SavedMeasurements';
import { AddPointTool } from './AddPointTool';
import { ClipBoxTool } from './ClipBoxTool';

export const PointCloud = ({
  layers,
  visible,
  project,
}: {
  layers: LayerData[];
  visible?: boolean;
  controlRef: RefObject<CameraControls | null>;
  project?: GetProjectQuery['project'];
}) => {
  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const scene = useThree((state) => state.scene);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const [pickedPoint, setPickedPoint] = useAtom(pickedPointAtom);

  const potree = useMemo(() => {
    const potree = new Potree('v2');

    // @ts-expect-error https://github.com/pnext/three-loader/pull/231
    potree.updateChildVisibility = function updateChildVisibility(
      queueItem: QueueItem,
      // @ts-expect-error temp fix
      priorityQueue: BinaryHeap<QueueItem>,
      pointCloud: PointCloudOctree,
      node: IPointCloudTreeNode,
      cameraPosition: THREE.Vector3,
      camera: THREE.Camera,
      halfHeight: number,
    ): void {
      const children = node.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child === null) continue;

        const sphere = child.boundingSphere;
        const distance = sphere.center.distanceTo(cameraPosition);
        const radius = sphere.radius;

        let projectionFactor = 0.0;

        if (camera.type === 'PerspectiveCamera') {
          const perspective = camera as THREE.PerspectiveCamera;
          const fov = (perspective.getEffectiveFOV() * Math.PI) / 180.0; // accounts for zoom
          const slope = Math.tan(fov / 2.0);
          projectionFactor = halfHeight / (slope * distance);
        } else {
          const orthographic = camera as THREE.OrthographicCamera;
          projectionFactor = (2 * halfHeight * orthographic.zoom) / (orthographic.top - orthographic.bottom);
        }

        const screenPixelRadius = radius * projectionFactor;

        if (screenPixelRadius < pointCloud.minNodePixelSize) continue;

        const weight = distance < radius ? Number.MAX_VALUE : screenPixelRadius + 1 / distance;

        priorityQueue.push(new QueueItem(queueItem.pointCloudIndex, weight, child, node));
      }
    };

    return potree;
  }, []);
  const [pointClouds, setPointClouds] = useState<PointCloudOctree[]>([]);

  useEffect(() => {
    let _pcos: PointCloudOctree[] = [];

    const abortController = new AbortController();

    const fetchPointClouds = async () => {
      _pcos = await Promise.all(
        layers.map((layer) => {
          if (!layer.root) throw new Error('No root file specified for point cloud layer ' + layer.name);

          return new Promise<PointCloudOctree>((resolve) =>
            potree
              .loadPointCloud(
                layer.root?.includes('metadata.json') ? layer.root : `${layer.root!}/metadata.json`,
                (relativeUrl) => `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${relativeUrl}`,
                (input: RequestInfo, init?: RequestInit) => {
                  if (String(input).endsWith('metadata.json') && layer.metadata?.potreeMetadata) {
                    const mockResponse = new Response(JSON.stringify(layer.metadata.potreeMetadata), {
                      status: 200,
                      headers: { 'Content-Type': 'application/json' },
                    });
                    return Promise.resolve(mockResponse);
                  }
                  return fetch(input, {
                    ...init,
                    credentials: 'include',
                  });
                },
              )
              .then((pco) => {
                pco.up.set(0, 0, 1);

                if (
                  (pco.pcoGeometry.loader as NodeLoader).metadata.attributes
                    .find((a) => a.name === 'rgb')
                    ?.max.some((v) => v > 0)
                ) {
                  // todo: remove rgb option
                }

                pco.material.minSize = 1;
                pco.material.maxSize = 100;
                pco.material.shape = PointShape.CIRCLE;

                // pco.material.transparent = true;
                // pco.material.pointOpacityType = PointOpacityType.ATTENUATED;
                // pco.material.opacity = 0.5;
                // pco.material.useEDL = true;

                if (viewerSettings?.pointCloudPointSizeType === 'FIXED') {
                  pco.material.size = viewerSettings?.pointCloudPointSize || 1.0;
                  pco.material.pointSizeType = PointSizeType.FIXED;
                } else {
                  pco.material.size = 1.0;
                  pco.material.pointSizeType = PointSizeType.ADAPTIVE;
                }

                pco.userData.layerId = layer.id;

                // Override updateMaterial because of FOV bug in library. https://github.com/pnext/three-loader/pull/231
                pco.material.updateMaterial = function (
                  octree: PointCloudOctree,
                  visibleNodes: PointCloudOctreeNode[],
                  camera: THREE.Camera,
                  renderer: THREE.WebGLRenderer,
                ) {
                  const material = this as PointCloudMaterial;
                  const pixelRatio = renderer.getPixelRatio();

                  if (camera.type === 'PerspectiveCamera') {
                    const perspectiveCamera = camera as THREE.PerspectiveCamera;
                    material.fov = perspectiveCamera.getEffectiveFOV() * (Math.PI / 180);
                  } else {
                    material.fov = Math.PI / 2; // will result in slope = 1 in the shader
                  }

                  const renderTarget = renderer.getRenderTarget();
                  if (renderTarget !== null) {
                    material.screenWidth = renderTarget.width;
                    material.screenHeight = renderTarget.height;
                  } else {
                    material.screenWidth = renderer.domElement.clientWidth * pixelRatio;
                    material.screenHeight = renderer.domElement.clientHeight * pixelRatio;
                  }

                  if (material.useDrawingBufferSize) {
                    const helperVec2 = new THREE.Vector2();
                    renderer.getDrawingBufferSize(helperVec2);
                    material.screenWidth = helperVec2.width;
                    material.screenHeight = helperVec2.height;
                  }

                  const maxScale = Math.max(octree.scale.x, octree.scale.y, octree.scale.z);
                  material.spacing = octree.pcoGeometry.spacing * maxScale;

                  const helperVec3 = new THREE.Vector3();
                  material.octreeSize = octree.pcoGeometry.boundingBox.getSize(helperVec3).x;

                  if (
                    material.pointSizeType === PointSizeType.ADAPTIVE ||
                    material.pointColorType === PointColorType.LOD
                  ) {
                    // Call the private method through type assertion
                    (material as any).updateVisibilityTextureData(visibleNodes);
                  }
                };

                resolve(pco);
              }),
          );
        }),
      );

      if (abortController.signal.aborted) return;

      if (_pcos.length) scene.add(..._pcos);
      setPointClouds(_pcos);
    };

    fetchPointClouds();

    return () => {
      abortController.abort();
      _pcos.forEach((_pco) => scene.remove(_pco));
      pointClouds.forEach((pco) => {
        scene.remove(pco);
      });
      setPointClouds([]);
    };
  }, [layers.map((l) => l.root).join(',')]);

  useFrame(() => {
    // When using an orthographic camera the adaptive point-size formula
    // (designed for perspective projection) produces oversized points for
    // geometry that is close in world-space.  Switch every PCO to FIXED
    // sizing with a zoom-derived size so points stay visually consistent.
    const isOrtho = camera instanceof THREE.OrthographicCamera;
    pointClouds.forEach((pc) => {
      if (isOrtho) {
        if (pc.material.pointSizeType !== PointSizeType.FIXED) {
          pc.material.pointSizeType = PointSizeType.FIXED;
        }
        // Scale point size with ortho zoom so zooming in/out feels natural.
        // Clamp to a reasonable range to avoid invisible or giant points.
        const orthoSize = Math.max(1, Math.min(4, camera.zoom * 0.15));
        pc.material.size = orthoSize;
      } else {
        // Restore user-chosen settings when switching back to perspective
        if (viewerSettings?.pointCloudPointSizeType === 'FIXED') {
          if (pc.material.pointSizeType !== PointSizeType.FIXED) {
            pc.material.pointSizeType = PointSizeType.FIXED;
          }
          pc.material.size = viewerSettings?.pointCloudPointSize || 1.0;
        } else {
          if (pc.material.pointSizeType !== PointSizeType.ADAPTIVE) {
            pc.material.pointSizeType = PointSizeType.ADAPTIVE;
          }
          pc.material.size = 1.0;
        }
      }
    });

    potree.updatePointClouds(pointClouds, camera, renderer);
  });

  useEffect(() => {
    potree.pointBudget = viewerSettings?.pointCloudPointBudget;
    // Point size type/size is managed by useFrame when ortho is active,
    // so only apply user settings for perspective cameras.
    if (!(camera instanceof THREE.OrthographicCamera)) {
      pointClouds.forEach((pc) => {
        if (viewerSettings?.pointCloudPointSizeType === 'FIXED') {
          pc.material.pointSizeType = PointSizeType.FIXED;
          pc.material.size = viewerSettings?.pointCloudPointSize || 1.0;
        } else {
          pc.material.pointSizeType = PointSizeType.ADAPTIVE;
          pc.material.size = 1.0;
        }
      });
    }
  }, [
    pointClouds,
    camera,
    viewerSettings?.pointCloudPointBudget,
    viewerSettings?.pointCloudPointSizeType,
    viewerSettings?.pointCloudPointSize,
    potree,
  ]);

  useEffect(() => {
    pointClouds.forEach((pc) => {
      const layerSetting = layerSettings[pc.userData.layerId];

      if (!layerSetting) return;

      pc.material.opacity = visible ? 1 : 0;
      pc.material.pointOpacityType = 0;

      pc.material.pointColorType = layerSetting.pointCloudMaterialColorSource || 0;
      // @ts-ignore
      pc.material.gradient = gradients[layerSetting.pointCloudMaterialColor];

      if (layerSetting.pointCloudMaterialColorSource === 1 && layerSetting.pointCloudFixedColor) {
        pc.material.color = new THREE.Color(layerSetting.pointCloudFixedColor);
      }

      if (pc.material.pointColorType === PointColorType.CLASSIFICATION) {
        pc.material.useFilterByClassification = true;
        const classification: IClassification = { DEFAULT: new THREE.Vector4(0, 0, 0, 1) };
        layerSetting.pointCloudClasses.forEach((values, i) => {
          if (!values) return;
          const { color, isEnabled } = values;
          pc.material.classificationFilter[i] = isEnabled;
          if (color) classification[i] = new THREE.Vector4(...new THREE.Color(color).toArray(), 1);
        });
        pc.material.classification = classification;
      } else {
        pc.material.useFilterByClassification = false;
      }

      pc.material.updateShaderSource();
    });
  }, [pointClouds, layerSettings, visible]);

  useEffect(() => {
    const canvas = renderer.domElement;
    const DRAG_THRESHOLD = 5; // pixels
    let mouseDownPos: { x: number; y: number } | null = null;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 2) {
        mouseDownPos = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button !== 2 || !mouseDownPos) return;

      const dx = e.clientX - mouseDownPos.x;
      const dy = e.clientY - mouseDownPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < DRAG_THRESHOLD) {
        // Calculate normalized device coordinates
        const rect = canvas.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        // Configure raycaster
        raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

        // Pick point from pointclouds
        const hits = pointClouds.map((pointCloud) => pointCloud.pick(renderer, camera, raycaster.ray)).filter(Boolean);

        if (hits.length > 0 && hits[0]?.position) {
          // Calculate camera angle in 2D (0° = north/+Y, 90° = east/+X)
          const cameraDirection = new THREE.Vector3();
          camera.getWorldDirection(cameraDirection);
          const cameraAngle = (Math.atan2(cameraDirection.x, cameraDirection.y) * 180) / Math.PI;

          setPickedPoint({ ...hits[0], screenX: e.clientX, screenY: e.clientY, cameraAngle });
        }
      }

      mouseDownPos = null;
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('contextmenu', handleContextMenu);
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [camera, renderer, raycaster, pointClouds]);

  if (!pointClouds || pointClouds.length === 0) return null;

  return (
    <>
      <MeasuringTool origin={pointClouds[0].position} targets={pointClouds} projectId={project?.id} />
      <AreaMeasuringTool targets={pointClouds} projectId={project?.id} />
      {project?.id && <SavedMeasurements projectId={project.id} />}
      {project?.id && <AddPointToolWrapper origin={pointClouds[0].position} targets={pointClouds} project={project} />}
      {pickedPoint?.position && <PointX scale={0.1} position={pickedPoint.position} />}
      <ClipBoxTool pointClouds={pointClouds} />
      <PointCloudBoundingBoxes layers={layers} />
    </>
  );
};

// Wrapper component to handle add point mode state
const AddPointToolWrapper = ({
  origin,
  targets,
  project,
}: {
  origin: THREE.Vector3;
  targets: PointCloudOctree[];
  project: GetProjectQuery['project'];
}) => {
  const addPointMode = useAtomValue(addPointModeAtom);

  if (!addPointMode.enabled || !addPointMode.targetLayerId) return null;

  return (
    <AddPointTool
      origin={origin}
      targets={targets}
      enabled={addPointMode.enabled}
      srid={project.srid}
      targetLayerId={addPointMode.targetLayerId}
    />
  );
};

const gradients = {
  GRAYSCALE: [
    [0, new THREE.Color(0)],
    [1, new THREE.Color(16777215)],
  ],
  RAINBOW: [
    [0, new THREE.Color(4587702)],
    [0.16666666666666666, new THREE.Color(255)],
    [0.3333333333333333, new THREE.Color(65535)],
    [0.5, new THREE.Color(65280)],
    [0.6666666666666666, new THREE.Color(16776960)],
    [0.8333333333333334, new THREE.Color(16753408)],
    [1, new THREE.Color(16711680)],
  ],
  SPECTRAL: [
    [0, new THREE.Color(6114978)],
    [0.1, new THREE.Color(3311549)],
    [0.2, new THREE.Color(6734501)],
    [0.3, new THREE.Color(11263395)],
    [0.4, new THREE.Color(15136152)],
    [0.5, new THREE.Color(16777150)],
    [0.6, new THREE.Color(16703371)],
    [0.7, new THREE.Color(16625249)],
    [0.8, new THREE.Color(16018754)],
    [0.9, new THREE.Color(13974862)],
    [1, new THREE.Color(10289217)],
  ],
  PLASMA: [
    [0, new THREE.Color(3998619)],
    [0.1, new THREE.Color(6422694)],
    [0.2, new THREE.Color(8717990)],
    [0.3, new THREE.Color(10887063)],
    [0.4, new THREE.Color(12532098)],
    [0.5, new THREE.Color(13980525)],
    [0.6, new THREE.Color(15167066)],
    [0.7, new THREE.Color(16092485)],
    [0.8, new THREE.Color(16559409)],
    [0.9, new THREE.Color(16503076)],
    [1, new THREE.Color(15726625)],
  ],
  YELLOW_GREEN: [
    [0, new THREE.Color(2705496)],
    [0.1, new THREE.Color(2251371)],
    [0.2, new THREE.Color(1011324)],
    [0.3, new THREE.Color(33415)],
    [0.4, new THREE.Color(38286)],
    [0.5, new THREE.Color(2271631)],
    [0.6, new THREE.Color(4897931)],
    [0.7, new THREE.Color(7458436)],
    [0.8, new THREE.Color(10215291)],
    [0.9, new THREE.Color(13233523)],
    [1, new THREE.Color(16448110)],
  ],
  VIRIDIS: [
    [0, new THREE.Color(4456787)],
    [0.1, new THREE.Color(4727668)],
    [0.2, new THREE.Color(4211591)],
    [0.3, new THREE.Color(3432077)],
    [0.4, new THREE.Color(2717838)],
    [0.5, new THREE.Color(2134156)],
    [0.6, new THREE.Color(2271364)],
    [0.7, new THREE.Color(4505200)],
    [0.8, new THREE.Color(7983441)],
    [0.9, new THREE.Color(12377638)],
    [1, new THREE.Color(16639780)],
  ],
  INFERNO: [
    [0, new THREE.Color(1247796)],
    [0.1, new THREE.Color(3737954)],
    [0.2, new THREE.Color(6230638)],
    [0.3, new THREE.Color(8724587)],
    [0.4, new THREE.Color(11087454)],
    [0.5, new THREE.Color(13320521)],
    [0.6, new THREE.Color(15096878)],
    [0.7, new THREE.Color(16220944)],
    [0.8, new THREE.Color(16494098)],
    [0.9, new THREE.Color(16112459)],
    [1, new THREE.Color(16514724)],
  ],
};
